import { isSenderApp } from '@/lib/app-mode';

const TAB_ROOTS = new Set(['/', '/send', '/accounts', '/more']);

export type SenderTabId = 'now' | 'send' | 'accounts' | 'more';

export function senderSectionTail(pathname: string): string {
	const path = pathname.split('?')[0] ?? pathname;
	if (path === '/sender') return '/';
	if (path.startsWith('/sender/')) {
		const tail = path.slice('/sender'.length);
		return tail.length > 0 ? tail : '/';
	}
	if (!path.startsWith('/')) return `/${path}`;
	return path.length > 0 ? path : '/';
}

function normalizeTail(tail: string): string {
	if (tail.length > 1 && tail.endsWith('/')) return tail.slice(0, -1);
	return tail || '/';
}

function panelPrefix(pathname: string): string {
	return pathname === '/sender' || pathname.startsWith('/sender/') ? '/sender' : '';
}

function withPrefix(pathname: string, next: string): string {
	const prefix = panelPrefix(pathname);
	if (!prefix) return next;
	const q = next.indexOf('?');
	const path = q === -1 ? next : next.slice(0, q);
	const search = q === -1 ? '' : next.slice(q);
	if (path === '/') return `${prefix}${search}`;
	return `${prefix}${path}${search}`;
}

/** Whether pathname belongs to the sender section (for shell back-button and nav gating). */
export function isSenderPath(pathname: string): boolean {
	if (isSenderApp) {
		return pathname !== '/auth' && !pathname.startsWith('/auth/');
	}
	return pathname === '/sender' || pathname.startsWith('/sender/');
}

export function isSenderTabRoot(pathname: string): boolean {
	return TAB_ROOTS.has(normalizeTail(senderSectionTail(pathname)));
}

/** Telegram BackButton stays hidden on the four tab roots. */
export function isSenderBackButtonHidden(pathname: string): boolean {
	return isSenderTabRoot(pathname);
}

export function isSenderTabsHidden(pathname: string): boolean {
	const tail = normalizeTail(senderSectionTail(pathname));
	if (tail === '/auth' || tail.startsWith('/auth/')) return true;
	if (tail === '/send/one') return true;
	if (/^\/runs\/[^/]+$/.test(tail)) return true;
	if (tail === '/scenarios/new' || /^\/scenarios\/[^/]+$/.test(tail)) return true;
	if (/^\/auth-jobs\/[^/]+$/.test(tail)) return true;
	return false;
}

export function resolveSenderTab(pathname: string, from?: string | null): SenderTabId | null {
	if (isSenderTabsHidden(pathname)) return null;
	const tail = normalizeTail(senderSectionTail(pathname));
	if (tail === '/console') {
		const fromTail = from ? normalizeTail(senderSectionTail(from)) : '/';
		if (fromTail === '/more' || fromTail.startsWith('/more/')) return 'more';
		return 'now';
	}
	if (tail === '/' || tail === '/runs') return 'now';
	if (tail === '/send') return 'send';
	if (tail === '/accounts') return 'accounts';
	if (
		tail === '/more' ||
		tail === '/scenarios' ||
		tail.startsWith('/scenarios/') ||
		tail === '/stats' ||
		tail === '/settings' ||
		tail === '/auth-jobs' ||
		tail.startsWith('/auth-jobs/')
	) {
		return 'more';
	}
	return 'now';
}

export function isSenderAuthJobDetailPath(pathname: string): boolean {
	const tail = normalizeTail(senderSectionTail(pathname));
	return /^\/auth-jobs\/[^/]+$/.test(tail);
}

export function resolveSenderBackPath(pathname: string): string {
	const tail = normalizeTail(senderSectionTail(pathname));
	if (/^\/runs\/[^/]+$/.test(tail)) return withPrefix(pathname, '/');
	if (/^\/auth-jobs\/[^/]+$/.test(tail)) return withPrefix(pathname, '/auth-jobs');
	if (tail === '/send/one' || tail === '/launch') return withPrefix(pathname, '/send?mode=one');
	if (tail === '/scenarios/new' || /^\/scenarios\/[^/]+$/.test(tail)) return withPrefix(pathname, '/scenarios');
	if (tail === '/runs' || tail === '/console') return withPrefix(pathname, '/');
	if (tail === '/scenarios' || tail === '/stats' || tail === '/settings' || tail === '/auth-jobs') {
		return withPrefix(pathname, '/more');
	}
	return withPrefix(pathname, '/');
}
